/**
 * application-edit-draft — the Bewerbungs-Review's LOCAL, per-user, per-application draft
 * for the ONE cover-letter paragraph the reader is rewriting.
 *
 * The review's paragraph editor is the longest-lived unsaved-work window in the app that is
 * not already protected: the reader opens a paragraph, rewrites it in their own words — a
 * German cover-letter paragraph, so paragraphs, not a line — and nothing reaches the server
 * until »Vorschlagen« POSTs `/applications/edit/propose`. Until then the text lives only in
 * the `editing` signal, and the page dies from a plain reload or from the data-plane 401
 * route-to-login round-trip (`services.ts`) the app triggers itself.
 *
 * Keyed per APPLICATION as well as per user: a reader with two applications open in two tabs
 * must not have one tab's rewrite surface in the other. The storage plumbing and the
 * untrusted-boundary discipline live in `local-draft.ts`; this module owns the shape and its
 * structural guard.
 */

import { createLocalDraftStore, type LocalDraftStore } from "./local-draft"

/** The persisted editor state: which paragraph, the text so far, and its freshness clock. */
export interface ApplicationEditDraft {
  /** Index of the cover-letter paragraph being rewritten. */
  index: number
  /** The in-progress rewrite (empty allowed — clearing the box is a legitimate edit). */
  text: string
  /** When this draft was last touched (ISO-8601). */
  updatedAt: string
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null

/**
 * A total, dependency-free guard for a persisted editor draft. Structural only: the index has
 * to be a real paragraph slot (the surface re-checks it against the letter it actually loaded)
 * and the text has to be a string; the propose endpoint re-validates everything server-side.
 */
export function isApplicationEditDraft(v: unknown): v is ApplicationEditDraft {
  if (!isRecord(v)) return false
  const index = v["index"]
  return (
    typeof index === "number" &&
    Number.isInteger(index) &&
    index >= 0 &&
    typeof v["text"] === "string" &&
    typeof v["updatedAt"] === "string"
  )
}

export type ApplicationEditDraftStore = LocalDraftStore<ApplicationEditDraft>

const KEY_PREFIX = "career:application-review:edit-draft:v1:"

/** A per-user, per-application editor-draft store, guarded by {@link isApplicationEditDraft}. */
export function createApplicationEditDraftStore(
  applicationUrn: string,
  userId: string | null,
  explicitStorage?: Storage,
): ApplicationEditDraftStore {
  return createLocalDraftStore(`${KEY_PREFIX}${applicationUrn}:`, userId, isApplicationEditDraft, explicitStorage)
}
